import { Button } from '@/components/ui/button';

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useData, type SensorType } from '@/contexts/data-context';
import {
  Activity,
  AlertTriangle,
  Battery,
  CheckCircle,
  Database,
  Play,
  Pause,
  Thermometer,
  Droplets,
  Gauge,
  Zap,
  Sun,
  Wind,
  Users,
} from 'lucide-react';
import SensorChart from './sensor-chart';
import SensorOverview from './sensor-overview';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

export default function DataMonitoringPanel() {
  const { sensors, selectedEnvironment, setSelectedEnvironment, isRealTime, setIsRealTime, getSensorsByStatus } =
    useData();

  const [selectedSensor, setSelectedSensor] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  const criticalSensors = getSensorsByStatus('critical');
  const warningSensors = getSensorsByStatus('warning');
  const normalSensors = getSensorsByStatus('normal');

  const getSensorIcon = (type: SensorType) => {
    switch (type) {
      case 'temperature':
        return <Thermometer className="w-4 h-4" />;
      case 'humidity':
        return <Droplets className="w-4 h-4" />;
      case 'pressure':
        return <Gauge className="w-4 h-4" />;
      case 'light':
        return <Sun className="w-4 h-4" />;
      case 'electrical':
      case 'power':
      case 'voltage':
      case 'current':
        return <Zap className="w-4 h-4" />;
      case 'co2':
      case 'pm25':
      case 'pm10':
      case 'voc':
        return <Wind className="w-4 h-4" />;
      case 'noise':
        return <Activity className="w-4 h-4" />;
      case 'motion':
        return <Activity className="w-4 h-4" />;
      case 'occupancy':
        return <Users className="w-4 h-4" />;
      case 'water':
        return <Droplets className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'critical':
        return <AlertTriangle className="w-4 h-4 text-destructive" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-warning" />;
      default:
        return <CheckCircle className="w-4 h-4 text-success" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'critical':
        return 'border-destructive bg-destructive/10';
      case 'warning':
        return 'border-warning bg-warning/10';
      default:
        return 'border-success bg-success/10';
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-shrink-0 p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Database className="w-5 h-5" />
            Data Monitor
          </h2>
          <div className="flex items-center gap-2">
            {isRealTime ? (
              <Badge variant="outline" className="bg-success/10 text-success border-success">
                <Play className="w-3 h-3 mr-1" /> Live
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-muted/50 text-muted-foreground">
                <Pause className="w-3 h-3 mr-1" /> Paused
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        <div className="h-full p-4 space-y-4 overflow-y-auto">
          {/* Environment Selection */}
          <Card className="flex-shrink-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Environment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Select value={selectedEnvironment} onValueChange={setSelectedEnvironment}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="datacenter">Data Center</SelectItem>
                  <SelectItem value="greenhouse">Greenhouse</SelectItem>
                  <SelectItem value="charging">EV Charging Station</SelectItem>
                  <SelectItem value="smartbuilding">Smart Building</SelectItem>
                  <SelectItem value="industrial">Industrial Facility</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center justify-between">
                <Label htmlFor="realtime" className="text-sm">
                  Real-time Updates
                </Label>
                <Switch id="realtime" checked={isRealTime} onCheckedChange={setIsRealTime} />
              </div>
            </CardContent>
          </Card>

          {/* Sensor Data Tabs */}
          <div className="flex-1 min-h-0">
            <Tabs defaultValue="overview" className="h-full flex flex-col">
              <div className="flex-shrink-0">
                <TabsList className="w-full">
                  <TabsTrigger value="overview" className="flex-1">
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="alerts" className="flex-1 relative">
                    Alerts
                    {(criticalSensors.length > 0 || warningSensors.length > 0) && (
                      <Badge variant="destructive" className="ml-1 h-5 min-w-5 p-0 flex items-center justify-center">
                        {criticalSensors.length + warningSensors.length}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="details" className="flex-1">
                    Details
                  </TabsTrigger>
                </TabsList>
              </div>

              <div className="flex-1 min-h-0 mt-4">
                <TabsContent value="overview" className="h-full">
                  <div className="h-full overflow-y-auto">
                    <div className="space-y-4">
                      <SensorOverview />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="alerts" className="h-full">
                  <div className="h-full overflow-y-auto">
                    <div className="space-y-4">
                      {criticalSensors.length === 0 && warningSensors.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <CheckCircle className="w-12 h-12 mx-auto mb-4 text-success" />
                          <p>All systems normal</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {criticalSensors.length > 0 && (
                            <div>
                              <h3 className="text-sm font-medium mb-2 text-destructive flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4" /> Critical Alerts
                              </h3>
                              <div className="space-y-2">
                                {criticalSensors.map((sensor) => (
                                  <Card key={sensor.id} className="border-destructive bg-destructive/5">
                                    <CardContent className="p-3">
                                      <div className="flex justify-between items-center">
                                        <div className="min-w-0 flex-1">
                                          <div className="font-medium flex items-center gap-1">
                                            {getSensorIcon(sensor.type)}
                                            <span className="truncate">{sensor.name}</span>
                                          </div>
                                          <div className="text-sm text-muted-foreground">
                                            {sensor.value.toFixed(1)} {sensor.unit} (Range: {sensor.min}-{sensor.max}{' '}
                                            {sensor.unit})
                                          </div>
                                        </div>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="h-7 ml-2 flex-shrink-0"
                                          onClick={() => setSelectedSensor(sensor.id)}
                                        >
                                          View
                                        </Button>
                                      </div>
                                    </CardContent>
                                  </Card>
                                ))}
                              </div>
                            </div>
                          )}

                          {warningSensors.length > 0 && (
                            <div>
                              <h3 className="text-sm font-medium mb-2 text-warning flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4" /> Warnings
                              </h3>
                              <div className="space-y-2">
                                {warningSensors.map((sensor) => (
                                  <Card key={sensor.id} className="border-warning bg-warning/5">
                                    <CardContent className="p-3">
                                      <div className="flex justify-between items-center">
                                        <div className="min-w-0 flex-1">
                                          <div className="font-medium flex items-center gap-1">
                                            {getSensorIcon(sensor.type)}
                                            <span className="truncate">{sensor.name}</span>
                                          </div>
                                          <div className="text-sm text-muted-foreground">
                                            {sensor.value.toFixed(1)} {sensor.unit} (Range: {sensor.min}-{sensor.max}{' '}
                                            {sensor.unit})
                                          </div>
                                        </div>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="h-7 ml-2 flex-shrink-0"
                                          onClick={() => setSelectedSensor(sensor.id)}
                                        >
                                          View
                                        </Button>
                                      </div>
                                    </CardContent>
                                  </Card>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="details" className="h-full">
                  <div className="h-full overflow-y-auto">
                    <div className="space-y-4">
                      {/* Sensor selection */}
                      <div className="sticky top-0 bg-background/95 backdrop-blur-sm p-2 -m-2 z-10">
                        <Select value={selectedSensor || ''} onValueChange={setSelectedSensor}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a sensor" />
                          </SelectTrigger>
                          <SelectContent>
                            {sensors.map((sensor) => (
                              <SelectItem key={sensor.id} value={sensor.id}>
                                <div className="flex items-center gap-2">
                                  {getSensorIcon(sensor.type)}
                                  <span>{sensor.name}</span>
                                  {sensor.status !== 'normal' && (
                                    <span className="ml-auto">{getStatusIcon(sensor.status)}</span>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Sensor details */}
                      {selectedSensor && <SensorDetails sensorId={selectedSensor} />}
                    </div>
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}

function SensorDetails({ sensorId }: { sensorId: string }) {
  const { sensors } = useData();
  const sensor = sensors.find((s) => s.id === sensorId);

  if (!sensor) return null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-base flex items-center gap-2">
              {sensor.type === 'temperature' && <Thermometer className="w-4 h-4" />}
              {sensor.type === 'humidity' && <Droplets className="w-4 h-4" />}
              {sensor.type === 'pressure' && <Gauge className="w-4 h-4" />}
              {(sensor.type === 'electrical' ||
                sensor.type === 'power' ||
                sensor.type === 'voltage' ||
                sensor.type === 'current') && <Zap className="w-4 h-4" />}
              {sensor.name}
            </CardTitle>
            <Badge
              variant={
                sensor.status === 'critical' ? 'destructive' : sensor.status === 'warning' ? 'default' : 'outline'
              }
              color={sensor.status === 'critical' ? 'destructive' : sensor.status === 'warning' ? 'warning' : 'success'}
            >
              {sensor.status}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center">
            <div className="text-3xl font-bold">
              {sensor.value.toFixed(getPrecision(sensor.type))} {sensor.unit}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Range: {sensor.min} - {sensor.max} {sensor.unit}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Last Updated:</span>
              <span>{sensor.lastUpdated.toLocaleTimeString()}</span>
            </div>

            <div className="flex justify-between text-sm">
              <span>Accuracy:</span>
              <span>{sensor.accuracy.toFixed(1)}%</span>
            </div>

            {sensor.batteryLevel !== undefined && (
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Battery:</span>
                  <span className={sensor.batteryLevel < 20 ? 'text-destructive' : ''}>
                    {sensor.batteryLevel.toFixed(0)}%
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Battery className="w-4 h-4 text-muted-foreground" />
                  <Progress
                    value={sensor.batteryLevel}
                    className={`h-2 ${sensor.batteryLevel < 20 ? 'bg-destructive/20' : ''}`}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-between text-sm">
              <span>Status:</span>
              <span className={sensor.isOnline ? 'text-success' : 'text-destructive'}>
                {sensor.isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
          </div>

          <div className="h-[250px]">
            <SensorChart sensorId={sensorId} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function getPrecision(type: SensorType): number {
  switch (type) {
    case 'temperature':
      return 1;
    case 'humidity':
      return 1;
    case 'pressure':
      return 0;
    case 'voltage':
      return 1;
    case 'current':
      return 2;
    case 'power':
      return 2;
    case 'co2':
      return 0;
    case 'pm25':
    case 'pm10':
      return 1;
    default:
      return 1;
  }
}
